import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import api from "../config/axiosConfig";
import { useAuth } from "./AuthContext";

const UserContext = createContext();

const initialState = {
  progression: [],
  progressionById: {},
  trainingKitData: [],
  loading: false,
  error: null,
  lastFetch: {
    progression: null,
    trainingKit: {},
    userData: null,
  },
  enrolledParcours: [],  
};

const ACTIONS = {
  FETCH_PROGRESSION_START: "FETCH_PROGRESSION_START",
  FETCH_PROGRESSION_SUCCESS: "FETCH_PROGRESSION_SUCCESS",
  FETCH_PROGRESSION_ERROR: "FETCH_PROGRESSION_ERROR",
  RESET_PROGRESSION: "RESET_PROGRESSION",
  SET_PROGRESSION_BY_ID: "SET_PROGRESSION_BY_ID",
  FETCH_TRAINING_KIT_REQUEST: "FETCH_TRAINING_KIT_REQUEST",
  FETCH_TRAINING_KIT_SUCCESS: "FETCH_TRAINING_KIT_SUCCESS",
  FETCH_TRAINING_KIT_FAILURE: "FETCH_TRAINING_KIT_FAILURE",
  UPDATE_PESAS_SUCCESS: "UPDATE_PESAS_SUCCESS",
  UPDATE_PESAS_FAILURE: "UPDATE_PESAS_FAILURE",
  UPDATE_LAST_FETCH_TRAINING_KIT: "UPDATE_LAST_FETCH_TRAINING_KIT",
  FETCH_USER_DATA_START: "FETCH_USER_DATA_START",
  FETCH_USER_DATA_SUCCESS: "FETCH_USER_DATA_SUCCESS",
  FETCH_USER_DATA_FAILURE: "FETCH_USER_DATA_FAILURE",
  FETCH_ENROLLED_PARCOURS_SUCCESS: "FETCH_ENROLLED_PARCOURS_SUCCESS",
};

const userReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.FETCH_PROGRESSION_START:
      return { ...state, loading: true, error: null };
    case ACTIONS.FETCH_PROGRESSION_SUCCESS:
      return {
        ...state,
        loading: false,
        progression: action.payload.list,
        progressionById: action.payload.byId,
        lastFetch: { ...state.lastFetch, progression: Date.now() },
      };
    case ACTIONS.FETCH_PROGRESSION_ERROR:
      return { ...state, loading: false, error: action.payload };
    case ACTIONS.RESET_PROGRESSION:
      return { ...state, progression: [], error: null };
    case ACTIONS.FETCH_TRAINING_KIT_REQUEST:
      return { ...state, loading: true, error: null };
    case ACTIONS.FETCH_TRAINING_KIT_SUCCESS:
      console.log('Training kit data fetched successfully:', action.payload);
      return { ...state, trainingKitData: action.payload || [], loading: false };
    case ACTIONS.FETCH_TRAINING_KIT_FAILURE:
      console.log('Failed to fetch training kit data:', action.payload);
      return { ...state, loading: false, error: action.payload };
    case ACTIONS.UPDATE_LAST_FETCH_TRAINING_KIT:
      return {
        ...state,
        lastFetch: {
          ...state.lastFetch,
          trainingKit: {
            ...state.lastFetch.trainingKit,
            [action.payload.key]: action.payload.timestamp,
          },
        },
      };
    case ACTIONS.UPDATE_PESAS_SUCCESS:
      return {
        ...state,
        user: { ...state.user, pesas: action.payload.pesas },
      };
    case ACTIONS.UPDATE_PESAS_FAILURE:
      return { ...state, error: action.payload };
    case ACTIONS.FETCH_USER_DATA_START:
      return { ...state, loading: true, error: null };
    case ACTIONS.FETCH_USER_DATA_SUCCESS:
      return {
        ...state,
        loading: false,        
        lastFetch: { ...state.lastFetch, userData: Date.now() },
      };
    case ACTIONS.FETCH_USER_DATA_FAILURE:
      return { ...state, loading: false, error: action.payload };
    case ACTIONS.FETCH_ENROLLED_PARCOURS_SUCCESS:
      return {
        ...state,
        enrolledParcours: action.payload
      };
    default:
      return state;
  }
};

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  const fetchWithRetry = async (url, retries = 3, baseDelay = 2000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await api.get(url);
        return response;
      } catch (error) {
        if (error.response?.status === 429 && i < retries - 1) {
          const waitTime = baseDelay * Math.pow(2, i);
          console.log(`Erreur 429 sur ${url}, retry dans ${waitTime}ms`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
          continue;
        }
        throw error;
      }
    }
  };

  const fetchUserProgression = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) {
      dispatch({ type: ACTIONS.RESET_PROGRESSION });
      return null;
    }

    const now = Date.now();
    if (!forceRefresh && state.lastFetch.progression && now - state.lastFetch.progression < 5 * 60 * 1000) {
      return state.progression;
    }

    dispatch({ type: ACTIONS.FETCH_PROGRESSION_START });

    try {
      const response = await fetchWithRetry("/users/me/progression");

      let normalizedProgression = [];
      let progressionById = {};

      
      if (response.status === 404 || !response.data) {
        console.log("Aucune progression legacy trouvée - OK si utilise seulement les parcours");
        const payload = { list: [], byId: {} };
        dispatch({ type: ACTIONS.FETCH_PROGRESSION_SUCCESS, payload });
        return payload;
      }

      const data = Array.isArray(response.data) ? response.data : [response.data];
      normalizedProgression = data
        .filter(prog => prog && prog.parcoursId)
        .map((prog) => ({
          ...prog,
          parcoursId: prog.parcoursId?._id
            ? { _id: prog.parcoursId._id.toString(), nom: prog.parcoursId.nom, description: prog.parcoursId.description }
            : { _id: prog.parcoursId.toString() },
        }));

      // NORMALISATION progressionById
      normalizedProgression.forEach(parcours => {
        Object.keys(parcours).forEach(key => {
          if (/^[0-9a-f]{24}$/.test(key)) {
            progressionById[key] = {
              progress: parcours[key].progress, 
              status: parcours[key].status,
              score: parcours[key].score,
              pointsEarned: parcours[key].pointsEarned,
            };
          }
        });

        [...(parcours.schoolActivities || []), ...(parcours.culturalActivities || [])].forEach(act => {
          if (act.activityId) {
            progressionById[act.activityId] = {
              progress: act.progressionGlobale,
              status: act.status,
              score: act.score,
              pointsEarned: act.pointsEarned,
            };
          }
        });
      });

      const payload = { list: normalizedProgression, byId: progressionById };
      dispatch({ type: ACTIONS.FETCH_PROGRESSION_SUCCESS, payload });
      return payload;

    } catch (error) {
      console.error("fetchUserProgression - Erreur:", error.response?.data || error.message);
      
      
      if (error.response?.status === 404) {
        const payload = { list: [], byId: {} };
        dispatch({ type: ACTIONS.FETCH_PROGRESSION_SUCCESS, payload });
        return payload;
      }
      
      dispatch({ type: ACTIONS.FETCH_PROGRESSION_ERROR, payload: error.message });
      return null;
    }
  }, [isAuthenticated, state.lastFetch.progression]);

  const fetchTrainingKitData = useCallback(async (parcoursIds, difficulty) => {
    const ids = Array.isArray(parcoursIds) ? parcoursIds : [parcoursIds].filter(Boolean);
    const cacheKey = `${ids.sort().join(',')}-${difficulty}`;
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000;

    // Si cache valide → ne touche pas au loading
    if (state.lastFetch.trainingKit[cacheKey] && now - state.lastFetch.trainingKit[cacheKey] < CACHE_DURATION) {
      return;
    }

    // Toujours démarrer le loading
    dispatch({ type: ACTIONS.FETCH_TRAINING_KIT_REQUEST });

    // Si aucun ID → terminer immédiatement
    if (!ids.length) {
      dispatch({ type: ACTIONS.FETCH_TRAINING_KIT_SUCCESS, payload: [] });
      dispatch({
        type: 'UPDATE_LAST_FETCH_TRAINING_KIT',
        payload: { key: cacheKey, timestamp: now },
      });
      return;
    }

    try {
      const response = await api.get('/training-activities', {
        params: { parcoursIds: ids.join(','), difficulty },
      });

      const newData = response.data || [];
      if (JSON.stringify(state.trainingKitData) !== JSON.stringify(newData)) {
        dispatch({ type: ACTIONS.FETCH_TRAINING_KIT_SUCCESS, payload: newData });
      }

      dispatch({
        type: 'UPDATE_LAST_FETCH_TRAINING_KIT',
        payload: { key: cacheKey, timestamp: now },
      });
    } catch (error) {
      dispatch({ type: ACTIONS.FETCH_TRAINING_KIT_FAILURE, payload: error.message });
    }
  }, [state.trainingKitData, state.lastFetch.trainingKit]);

  const updatePesas = async (newPesas) => {
    try {
      const response = await api.put('/users/me/updatePesasInDashboard', { pesas: newPesas });
      dispatch({ type: ACTIONS.UPDATE_PESAS_SUCCESS, payload: { pesas: response.data.user.pesas } });
      toast.success(`Pesas mis à jour ! Total: ${response.data.user.pesas}`);
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_PESAS_FAILURE, payload: error.response?.data || error.message });
      toast.error('Erreur lors de la mise à jour des Pesas.');
    }
  };

  const refreshUserData = useCallback(async () => {
    if (!isAuthenticated) {
      console.log("Utilisateur non authentifié, données utilisateur réinitialisées");
      dispatch({
        type: ACTIONS.FETCH_USER_DATA_SUCCESS,
        payload: {},
      });
      return;
    }

    dispatch({ type: ACTIONS.FETCH_USER_DATA_START });
    try {
      const response = await fetchWithRetry("/users/me");
      
      dispatch({
        type: ACTIONS.FETCH_USER_DATA_SUCCESS,
        payload: response.data, // La réponse contient directement les champs propositions et negociations
      });
    } catch (error) {
      console.error("Erreur lors de la récupération des données utilisateur:", error);
      dispatch({ type: ACTIONS.FETCH_USER_DATA_FAILURE, payload: error.message });
      toast.error("Erreur lors de la récupération des données utilisateur.");
    }
  }, [isAuthenticated]);

  const fetchEnrolledParcours = useCallback(async (force = false) => {
  if (!isAuthenticated) {
    dispatch({ type: ACTIONS.FETCH_ENROLLED_PARCOURS_SUCCESS, payload: [] });
    return [];  // ← retour explicite
  }

  try {
    const response = await api.get('/my-parcours');

    // Normalisation stricte : on veut TOUJOURS un tableau
    const enrolledParcours = Array.isArray(response.data?.enrolledParcours)
      ? response.data.enrolledParcours
      : (response.data || []);  // fallback robuste si structure inattendue

    dispatch({ 
      type: ACTIONS.FETCH_ENROLLED_PARCOURS_SUCCESS, 
      payload: enrolledParcours 
    });

    return enrolledParcours;  // ← RETOUR EXPLICITE pour Promise.all

  } catch (error) {
    console.error("Erreur fetchEnrolledParcours:", error.response?.data || error.message);
    dispatch({ type: ACTIONS.FETCH_ENROLLED_PARCOURS_SUCCESS, payload: [] });
    return [];  // ← retour en cas d'erreur
  }
}, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated) {
      fetchUserProgression();
      fetchEnrolledParcours();
      refreshUserData();
    } else {
      dispatch({ type: ACTIONS.RESET_PROGRESSION });
      dispatch({ type: ACTIONS.FETCH_USER_DATA_SUCCESS});
    }
  }, [isAuthenticated, authLoading, fetchUserProgression, fetchEnrolledParcours, refreshUserData]);

  return (
    <UserContext.Provider
      value={{
        progression: state.progression,
        progressionById: state.progressionById,
        enrolledParcours: state.enrolledParcours,
        trainingKitData: state.trainingKitData,
        loading: state.loading,
        error: state.error,
        fetchUserProgression,
        fetchEnrolledParcours,
        fetchTrainingKitData,
        updatePesas,        
        refreshUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};